/**
 * Chart-of-account GL upload: collect spreadsheet columns that are not mapped by the ref template
 * (or not consumed by canonical header parsing) into `additionalAttributes` for POST …/chart-of-account/bulk.
 */

/** Canonical keys promoted to first-class COA fields by `glAccountMappedObjectsToParsedRows` — never duplicate in extras. */
export const GL_ACCOUNT_ROW_FIRST_CLASS_KEYS_UPPER = new Set([
  'GL_CODE',
  'GL_ACCOUNT',
  'GL_DESCRIPTION',
  'TRANSACTION_TYPE',
  'ACCOUNT_TYPE',
  'ACCOUNT_SUB_TYPE',
  'NORMAL_BALANCE',
  'GROUP_CODE',
  'CURRENCY',
  'GL_JOIN_FIELD',
]);

/** Headers read directly into first-class COA fields during non-template CSV/XLS parse. */
export const COA_HEADER_CONSUMED_FOR_BULK_PARSE = new Set([
  'GL_CODE',
  'GL_DESCRIPTION',
  'GL_ACCOUNT',
  'TRANSACTION_TYPE',
  'ACCOUNT_TYPE',
  'ACCOUNT_SUB_TYPE',
  'NORMAL_BALANCE',
  'GROUP_CODE',
  'CURRENCY',
  'GL_JOIN_FIELD',
]);

export function headerToAdditionalAttributesKey(headerLabel) {
  const s = String(headerLabel ?? '').trim();
  if (!s) return null;
  if (/^[A-Z0-9_]+$/.test(s)) return s;
  const norm = s
    .replace(/[_\s]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .toUpperCase();
  return norm || null;
}

export function coerceLooseCellValue(value) {
  if (value === '' || value == null) return undefined;
  if (typeof value === 'boolean' || typeof value === 'number') return value;
  const s = String(value).trim();
  if (s === '') return undefined;
  const lower = s.toLowerCase();
  if (lower === 'true') return true;
  if (lower === 'false') return false;
  if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(s)) {
    const n = Number(s);
    if (!Number.isNaN(n)) return n;
  }
  return s;
}

/** Active ref-template: Excel headers (as selected in UI) → lowercase set for matching row keys. */
export function getTemplateMappedExcelHeadersLower(template, excelColumnByTemplateField) {
  const set = new Set();
  Object.entries(template?.mappings || {}).forEach(([templateKeyRaw, spec]) => {
    if (!spec || spec.isActive === false) return;
    const templateKey = String(templateKeyRaw).trim();
    const pick =
      excelColumnByTemplateField?.[templateKey] ??
      excelColumnByTemplateField?.[String(templateKeyRaw)];
    if (pick && String(pick).trim() !== '') set.add(String(pick).trim().toLowerCase());
  });
  return set;
}

/**
 * Canonical field names produced by active template mappings (UPPERCASE), for excluding from extras.
 * @param {(templateKeyRaw: string, spec: object) => string | null} resolveCanonical
 */
export function getTemplateMappedCanonicalUpperSet(template, excelColumnByTemplateField, resolveCanonical) {
  const set = new Set();
  Object.entries(template?.mappings || {}).forEach(([templateKeyRaw, spec]) => {
    if (!spec || spec.isActive === false) return;
    const templateKey = String(templateKeyRaw).trim();
    const pick =
      excelColumnByTemplateField?.[templateKey] ??
      excelColumnByTemplateField?.[String(templateKeyRaw)];
    if (!pick || String(pick).trim() === '') return;
    const c = resolveCanonical(templateKeyRaw, spec);
    if (c) set.add(String(c).trim().toUpperCase());
  });
  return set;
}

/**
 * After `applyGlAccountRefTemplateToRows`, remaining keys are unmapped file columns and/or canonical outputs.
 * Exclude template-mapped Excel headers and template-produced canonical keys; store the rest as JSON metadata.
 */
export function buildAdditionalAttributesFromTemplateRow(row, excelHeadersLower, canonicalUpper) {
  const attrs = {};
  const reserved = new Set(['glsystem', 'glsystemversion']);
  Object.entries(row).forEach(([k, v]) => {
    if (v === '' || v == null) return;
    const kt = String(k).trim();
    const kl = kt.toLowerCase();
    if (reserved.has(kl)) return;
    if (excelHeadersLower.has(kl)) return;
    const ku = kt.toUpperCase();
    if (canonicalUpper.has(ku)) return;
    if (GL_ACCOUNT_ROW_FIRST_CLASS_KEYS_UPPER.has(ku)) return;
    const nk = headerToAdditionalAttributesKey(kt);
    if (!nk) return;
    const coerced = coerceLooseCellValue(v);
    if (coerced === undefined) return;
    attrs[nk] = coerced;
  });
  if (Object.keys(attrs).length === 0) return null;
  return attrs;
}

/** Non-template parse: any header not in COA_HEADER_CONSUMED_FOR_BULK_PARSE → additionalAttributes. */
export function buildAdditionalAttributesFromHeaderIndexRow(row, headerRow, headerIndexMap) {
  if (!headerIndexMap || !Array.isArray(headerRow) || !Array.isArray(row)) return null;
  const attrs = {};
  Object.entries(headerIndexMap).forEach(([hUpper, idx]) => {
    if (COA_HEADER_CONSUMED_FOR_BULK_PARSE.has(hUpper)) return;
    if (idx == null || idx < 0 || idx >= row.length) return;
    const coerced = coerceLooseCellValue(row[idx]);
    if (coerced === undefined) return;
    const label = headerRow[idx] != null ? String(headerRow[idx]).trim() : hUpper;
    const nk = headerToAdditionalAttributesKey(label) || hUpper;
    if (!nk) return;
    attrs[nk] = coerced;
  });
  if (Object.keys(attrs).length === 0) return null;
  return attrs;
}

/** Combination mapping: column indices used for GL code / description / account / known optionals are excluded. */
export function buildAdditionalAttributesFromCombinationRow(row, headerRow, consumedIndices) {
  if (!Array.isArray(headerRow) || !Array.isArray(row)) return null;
  const consumed = consumedIndices instanceof Set ? consumedIndices : new Set(consumedIndices || []);
  const attrs = {};
  headerRow.forEach((h, idx) => {
    if (consumed.has(idx)) return;
    const coerced = coerceLooseCellValue(row[idx]);
    if (coerced === undefined) return;
    const nk = headerToAdditionalAttributesKey(h);
    if (!nk) return;
    attrs[nk] = coerced;
  });
  if (Object.keys(attrs).length === 0) return null;
  return attrs;
}

/**
 * Drops null, undefined, and blank-string keys so S3 JSON / payloads only include populated values.
 * Omits {@code glSystem} / {@code glSystemVersion} (UI metadata; not from the spreadsheet).
 * Compacts {@code additionalAttributes} the same way; omits that key if nothing remains.
 */
export function compactGlAccountRowForUpload(row) {
  if (!row || typeof row !== 'object') return row;
  const out = {};
  Object.entries(row).forEach(([k, v]) => {
    if (k === 'glSystem' || k === 'glSystemVersion') return;
    if (v === undefined || v === null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    if (k === 'additionalAttributes' && typeof v === 'object' && v !== null && !Array.isArray(v)) {
      const inner = {};
      Object.entries(v).forEach(([ik, iv]) => {
        if (iv === undefined || iv === null) return;
        if (typeof iv === 'string' && iv.trim() === '') return;
        inner[ik] = iv;
      });
      if (Object.keys(inner).length > 0) out[k] = inner;
      return;
    }
    out[k] = v;
  });
  return out;
}

const S3_CORE_HEADER_KEYS = new Set(['GL_CODE', 'GL_DESCRIPTION', 'GL_ACCOUNT']);

/** Keys used only to build GL_CODE / GL_DESCRIPTION / GL_ACCOUNT — not emitted as separate S3 columns. */
const S3_ROW_INTERNAL_KEYS = new Set([
  'additionalAttributes',
  'glCode1',
  'glCode2',
  'glCode',
  'glDescription',
  'glAccount',
]);

/** Map parser field names (camelCase) to spreadsheet-style headers; pass through already UPPER_SNAKE keys. */
export function rowFieldKeyToS3HeaderKey(key) {
  const s = String(key ?? '').trim();
  if (!s) return null;
  if (/^[A-Z][A-Z0-9_]*$/.test(s)) return s;
  return s
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toUpperCase();
}

function normalizeS3CellValue(v) {
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? undefined : t;
  }
  return v;
}

/**
 * Shape for the JSON file uploaded to S3: spreadsheet-style UPPER_SNAKE keys, flat (no {@code additionalAttributes} wrapper).
 * Built from the compact parsed row used for bulk save.
 *
 * - Core GL columns are always derived the same way as bulk save.
 * - Any other non-empty field on {@code row} (e.g. future {@code someNewField}) becomes {@code SOME_NEW_FIELD} automatically.
 * - {@code additionalAttributes} entries are merged with their keys as in the file (typically UPPER_SNAKE); they do not overwrite keys already set.
 */
export function buildGlAccountRowForS3NativeKeys(row) {
  if (!row || typeof row !== 'object') return row;
  const out = {};
  const glCode1 = String(row.glCode1 ?? '').trim();
  const glCode2Raw = String(row.glCode2 ?? '').trim();
  const glCodeCombined =
    glCode1 && glCode2Raw
      ? `${glCode1}.${glCode2Raw}`
      : (glCode1 || glCode2Raw || String(row.glCode ?? '').trim());
  if (glCodeCombined) out.GL_CODE = glCodeCombined;
  const glDescription = String(row.glDescription ?? '').trim();
  if (glDescription) out.GL_DESCRIPTION = glDescription;
  const glAccount = String(row.glAccount ?? '').trim();
  if (glAccount) out.GL_ACCOUNT = glAccount;

  Object.entries(row).forEach(([rawKey, v]) => {
    if (S3_ROW_INTERNAL_KEYS.has(rawKey)) return;
    const val = normalizeS3CellValue(v);
    if (val === undefined) return;
    const headerKey = rowFieldKeyToS3HeaderKey(rawKey);
    if (!headerKey) return;
    if (S3_CORE_HEADER_KEYS.has(headerKey)) return;
    if (Object.prototype.hasOwnProperty.call(out, headerKey)) return;
    out[headerKey] = val;
  });

  const extras = row.additionalAttributes;
  if (extras && typeof extras === 'object' && !Array.isArray(extras)) {
    Object.entries(extras).forEach(([k, v]) => {
      const val = normalizeS3CellValue(v);
      if (val === undefined) return;
      const key = String(k).trim();
      if (!key) return;
      const upper = key.toUpperCase();
      if (S3_CORE_HEADER_KEYS.has(upper)) return;
      if (Object.prototype.hasOwnProperty.call(out, key)) return;
      out[key] = val;
    });
  }
  return out;
}
